import config from "config";
import {
  Session,
  Clinic,
  ProcedureType,
  ViewType,
  TDailyAnalyticsCount,
  DeviceType,
  TFormName,
  TCTACopy,
  TFormVersion,
  UploadTypeType,
  FormNameType,
  TMessageSender,
} from "api/type";

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;
const urlPrefix = `${protocol}://${host}/clinics/v1/forms`;
const urlPrefixLlm = `${protocol}://${host}/api/v1/llm`;

export const createSession = async (clinicSdkKey: string): Promise<Session> => {
  const headers = {
    Authorization: `sdk-key ${clinicSdkKey}`,
  };
  const response = await fetch(`${urlPrefix}/sessions`, {
    method: "POST",
    headers,
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error: ${response.status} - ${errorText}`);
  }
  const j = await response.json();
  const session: Session = {
    id: j.id,
    accessToken: j.access_token,
  };
  return session;
};

export const getClinic = async (session: Session): Promise<Clinic> => {
  const headers = {
    Authorization: `session ${session.accessToken}`,
  };
  let response = await fetch(`${urlPrefix}/clinic-info`, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP Error: ${response.status} - ${text}`);
  }

  const clinic: Clinic = await response.json();
  if (!clinic.clinic_name) {
    // Would be better if this is handled in the backend
    const currentUrl = window.location.search;
    const urlSearchParams = new URLSearchParams(currentUrl);

    const clinicSdkKey = urlSearchParams.get("clinicSdkKey");
    clinic.clinic_name =
      clinicSdkKey ?? "fail to get clinic name or clinic sdk key";
  }
  return clinic;
};

export type TPostPatientsImagesBatchResponse = {
  batch_id: string;
  presigned_urls: { put_url: string; get_url: string }[];
};
export type TPostPatientsImagesBatchRequest = {
  images: {
    view_type: ViewType;
    procedure_types: ProcedureType[];
  }[];
};
export const postPatientsImagesBatch = async (
  session: Session,
  body: TPostPatientsImagesBatchRequest,
  imageBlobs: Blob[],
): Promise<TPostPatientsImagesBatchResponse> => {
  const headers = {
    Authorization: `session ${session.accessToken}`,
    "Content-Type": "application/json",
  };

  let response = await fetch(`${urlPrefix}/patients/images/batch`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP Error: ${response.status} - ${text}`);
  }
  let j = await response.json();

  await Promise.all(
    j.presigned_urls.map(async (item: { put_url: string }, index: number) => {
      const response = await fetch(item.put_url, {
        method: "PUT",
        headers: {
          "Content-Type": "image/png",
        },
        body: imageBlobs[index],
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP Error: ${response.status} - ${text}`);
      }
    }),
  );
  const result: TPostPatientsImagesBatchResponse = {
    batch_id: j.batch_id,
    presigned_urls: j.presigned_urls.map(
      (item: { get_url: string; put_url: string }) => ({
        put_url: item.put_url,
        get_url: item.get_url,
      }),
    ),
  };
  return result;
};

const checkUrlAvailability = async (url: string) => {
  while (true) {
    const response = await fetch(url, {
      method: "GET",
    });
    if (response.ok) return response;
    await new Promise((resolve) => setTimeout(resolve, 500));
  }
};

export type TPostTransformsResponse = {
  download_urls: string[];
  metadata: {
    implant_size_in_cc: number | null;
    success: boolean;
    reason: string;
  };
};
export type TPostTransformsRequest = {
  image_url: string;
  procedure_types: ProcedureType[];
  procedure_view: ViewType;
};
export const postTransforms = async (
  session: Session,
  body: TPostTransformsRequest,
): Promise<TPostTransformsResponse> => {
  const headers = {
    Authorization: `session ${session.accessToken}`,
    "Content-Type": "application/json",
  };
  const firstResponse = await fetch(`${urlPrefix}/transforms`, {
    method: "POST",
    body: JSON.stringify(body),
    headers,
  });
  if (!firstResponse.ok) {
    const text = await firstResponse.text();
    throw new Error(`HTTP Error: ${firstResponse.status} - ${text}`);
  }

  let firstJson = await firstResponse.json();

  const secondResponse = await checkUrlAvailability(firstJson.metadata_url);
  const secondJson = await secondResponse.json();

  if (!secondJson.status.success)
    return {
      download_urls: [],
      metadata: {
        implant_size_in_cc: secondJson.implant_size_in_cc,
        success: secondJson.status.success,
        reason: secondJson.status.reason,
      },
    };

  let urls = [];
  for (const url of firstJson.download_urls) {
    const response = await fetch(url, { method: "GET" });
    if (response.ok) urls.push(url);
  }

  return {
    download_urls: urls,
    metadata: {
      implant_size_in_cc: secondJson.implant_size_in_cc,
      success: secondJson.status.success,
      reason: secondJson.status.reason,
    },
  };
};

export type TPostTransformsBatchResponse = {
  batch_id: string;
  response: {
    presigned_urls: string[];
    image_data: {
      vew_type: ViewType;
      procedure_types: ProcedureType[];
      upload_type: string;
      batch_id: string;
    };
    metadata: {
      implant_size_in_cc: number | null;
      success: boolean;
      reason: string;
    };
  }[];
};
export type TPostTransformsBatchRequest = {
  batch_id: string;
  request: {
    image_url: string;
    procedure_view: ViewType;
    procedure_types: [ProcedureType];
  }[];
};
export const postTransformsBatch = async (
  session: Session,
  body: TPostTransformsBatchRequest,
): Promise<TPostTransformsBatchResponse> => {
  const headers = {
    Authorization: `session ${session.accessToken}`,
    "Content-Type": "application/json",
  };
  const firstResponse = await fetch(`${urlPrefix}/transforms/batch`, {
    method: "POST",
    body: JSON.stringify(body),
    headers,
  });

  if (!firstResponse.ok) {
    const text = await firstResponse.text();
    throw new Error(`HTTP Error: ${firstResponse.status} - ${text}`);
  }
  let firstJson = await firstResponse.json();

  const metadata_urlResponse = await Promise.all(
    firstJson.response.map(async (item: { metadata_url: string }) =>
      checkUrlAvailability(item.metadata_url),
    ),
  );

  const metadata_urlResponseJson = await Promise.all(
    (metadata_urlResponse as any).map((item: any) => item.json()),
  );

  const result: TPostTransformsBatchResponse = {
    batch_id: firstJson.batch_id,
    response: await Promise.all(
      metadata_urlResponseJson.map(async (item: any, index: number) => {
        const urls: string[] = [];
        for (const url of firstJson.response[index].presigned_urls) {
          try {
            const response = await fetch(url, { method: "GET" });
            if (response.ok) urls.push(url);
          } catch (error) {
            console.error(`Error fetching URL: ${url}`, error);
          }
        }

        return {
          presigned_urls: urls,
          image_data: firstJson.response[index].image_data,
          metadata: {
            implant_size_in_cc: item.implant_size_in_cc,
            success: item.status.success,
            reason: item.status.reason,
          },
        };
      }),
    ),
  };

  return result;
};

export type TPostDailyAnalyticsRequest = {
  form_name: string;
  daily_analytics_count_type: TDailyAnalyticsCount;
};

export const postDailyAnalytics = async (
  session: Session,
  body: TPostDailyAnalyticsRequest,
) => {
  const headers = {
    Authorization: `session ${session.accessToken}`,
    "Content-Type": "application/json",
  };

  let response = await fetch(`${urlPrefix}/daily-analytics`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP Error: ${response.status} - ${text}`);
  }
  let j = await response.json();

  return j;
};

export type TPostDailyDetailedAnalyticsRequest = {
  form_name: TFormName;
  cta_copy: TCTACopy;
  form_version: TFormVersion;
  device_type: DeviceType;
  daily_analytics_count_type: TDailyAnalyticsCount;
};

export const postDailyDetailedAnalytics = async (
  session: Session,
  body: TPostDailyAnalyticsRequest,
) => {
  const headers = {
    Authorization: `session ${session.accessToken}`,
    "Content-Type": "application/json",
  };

  let response = await fetch(`${urlPrefix}/daily-detailed-analytics`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP Error: ${response.status} - ${text}`);
  }
  let j = await response.json();

  return j;
};

export type TPostReferralDailyAnalyticsRequest = {
  form_name: string;
  referrer: string;
};
export const postReferralDailyAnalytics = async (
  session: Session,
  body: TPostReferralDailyAnalyticsRequest,
) => {
  const headers = {
    Authorization: `session ${session.accessToken}`,
    "Content-Type": "application/json",
  };

  let response = await fetch(`${urlPrefix}/referral-daily-analytics`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP Error: ${response.status} - ${text}`);
  }
  let j = await response.json();

  return j;
};

export type TPostNotifyUnsupportedProcedureRequest = {
  name: string;
  email: string;
  procedure_type: ProcedureType;
};
export const postNotifyUnsupportedProcedure = async (
  session: Session,
  body: TPostNotifyUnsupportedProcedureRequest,
) => {
  const headers = {
    Authorization: `session ${session.accessToken}`,
    "Content-Type": "application/json",
  };

  let response = await fetch(`${urlPrefix}/notify-unsupported-procedure`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP Error: ${response.status} - ${text}`);
  }
  let j = await response.json();

  return j;
};

export type TSubmitAdvertiseFormRequest = {
  form_user_id: string;
  form_name: FormNameType;
  is_debug: boolean;
  form_data: any;
  patient_record: {
    first_name: string;
    last_name: string;
    phone?: any;
    email?: string;
    location?: string;
    upload_type?: UploadTypeType;
    procedures_of_interest?: ProcedureType[];
  };
};
export type TSubmitAdvertiseFormResponse = {
  form_name: FormNameType;
  clinic_uuid: string;
  form_user_id: string;
  patient_uuid: string;
};
export const submitAdvertiseForm = async (
  session: Session,
  body: TSubmitAdvertiseFormRequest,
): Promise<TSubmitAdvertiseFormResponse> => {
  const headers = {
    Authorization: `session ${session?.accessToken}`,
    "Content-Type": "application/json",
  };
  try {
    const response = await fetch(`${urlPrefix}/submit`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP Error: ${response.status} - ${text}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error submitting form:", error);
    throw error;
  }
};

export const fetchImagePresignedUrl = async (presignedUrl: string) => {
  try {
    const response = await fetch(presignedUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.statusText}`);
    }
    const blob = await response.blob();
    const imageBlob = blob.slice(0, blob.size, "image/png");
    return imageBlob;
  } catch (error) {
    console.error("Error fetching or converting image:", error);
  }
};

export type TGetUserLocationResponse = {
  accuracy_radius: number;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  postal_code: string;
  subdivision: string;
};

export const getUserLocation = async (
  session: Session,
): Promise<TGetUserLocationResponse> => {
  const responseFirst = await fetch("https://api.ipify.org?format=json");

  if (!responseFirst.ok) {
    const text = await responseFirst.text();
    throw new Error(`HTTP Error: ${responseFirst.status} - ${text}`);
  }

  const jsonResponseFirst = await responseFirst.json();
  const ipAddresss = jsonResponseFirst.ip;

  const headers = {
    Authorization: `session ${session?.accessToken}`,
    "Content-Type": "application/json",
  };

  const responseSecond = await fetch(
    `${urlPrefix}/user-location/${ipAddresss}`,
    {
      method: "GET",
      headers,
    },
  );

  if (!responseSecond.ok) {
    const text = await responseSecond.text();
    throw new Error(`HTTP Error: ${responseSecond.status} - ${text}`);
  }

  const jsonResponseSecond = await responseSecond.json();
  return jsonResponseSecond;
};

export type TPostOpenAIChatRequest = {
  content: string;
  temperature: number;
  max_tokens: number;
  history: {
    prompt: string;
    response: string;
  }[];
  image_url?: string | null;
};
export type TPostOpenAIChatResponse = {
  prompt: string;
  response: string;
  questions: string[];
};

export const postOpenAIChat = async (
  session: Session,
  body: TPostOpenAIChatRequest,
): Promise<TPostOpenAIChatResponse> => {
  const headers = {
    Authorization: `session ${session?.accessToken}`,
    "Content-Type": "application/json",
  };
  const fetchUrl = `${urlPrefixLlm}/openai/chat`;

  const responseSecond = await fetch(fetchUrl, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!responseSecond.ok) {
    const errorText = await responseSecond.text();
    throw new Error(`HTTP error: ${responseSecond.status} - ${errorText}`);
  }

  const jsonSecond = await responseSecond.json();
  return jsonSecond;
};

export const getBeforeImageUrl = async (
  session: Session,
  selectedImage: Blob,
) => {
  const headers = {
    Authorization: `session ${session.accessToken}`,
  };
  // calling the endpoint to get the upload URL
  let response = await fetch(
    `${urlPrefix}/patient-images?timestamp=${Date.now()}`,
    {
      method: "POST",
      headers,
    },
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP Error: ${response.status} - ${text}`);
  }
  let j = await response.json();
  // Put file to the upload URL
  response = await fetch(j.upload_url, {
    method: "PUT",
    headers: {
      "Content-Type": "image/png",
    },
    body: selectedImage,
  });
  if (response.ok) {
    // get the download URL
    return j.download_url;
  } else {
    const text = await response.text();
    throw new Error(`HTTP Error: ${response.status} - ${text}`);
  }
};

export type TPostChatPlasticSurgeryRequest = {
  content: string;
  temperature: number;
  max_tokens: number;
  history: {
    prompt: string;
    response: string;
  }[];
  image_url?: string | null;
};
export type TPostChatPlasticSurgeryResponse = {
  prompt: string;
  response: string;
  questions: string[];
  should_simulate: boolean;
  should_collect_contact_info: boolean;
  procedure_to_simulate: ProcedureType | null;
};

export const postChatPlasticSurgery = async (
  session: Session,
  body: TPostChatPlasticSurgeryRequest,
): Promise<TPostChatPlasticSurgeryResponse> => {
  const headers = {
    Authorization: `session ${session?.accessToken}`,
    "Content-Type": "application/json",
  };
  const fetchUrl = `${urlPrefixLlm}/chat/plastic-surgery`;

  const responseSecond = await fetch(fetchUrl, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!responseSecond.ok) {
    const errorText = await responseSecond.text();
    throw new Error(`HTTP error: ${responseSecond.status} - ${errorText}`);
  }

  const jsonSecond = await responseSecond.json();
  return jsonSecond;
};

export type TPostLeadMessageRequest = {
  message: string;
  sender_type: TMessageSender;
};
export type TPostLeadMessageQuery = {
  patient_uuid: string;
};
export const postLeadMessage = async (
  session: Session,
  query: TPostLeadMessageQuery,
  body: TPostLeadMessageRequest,
): Promise<void> => {
  const headers = {
    Authorization: `session ${session?.accessToken}`,
    "Content-Type": "application/json",
  };

  try {
    const response = await fetch(
      `${urlPrefix}/patients/${query.patient_uuid}/lead-messages`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP Error: ${response.status} - ${text}`);
    }
  } catch (error) {
    console.error("Error sending message", error);
    throw error;
  }
};
